import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Card, Row, Col, Space, Button, Popconfirm, Tag } from 'antd';
import { CheckCircleOutlined, LogoutOutlined } from '@ant-design/icons';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePwd216Query } from './hooks/usePwd216Query';
import { usePwd216Approval } from './hooks/usePwd216Approval';
import Pwd216QueryBar from './components/Pwd216QueryBar';
import Pwd216PendingList from './components/Pwd216PendingList';
import Pwd216DetailPanel from './components/Pwd216DetailPanel';
import { pwd216Api } from '../../services/pwd216Api';
import {
  BatchAcceptanceBridge,
  pwd216f1AcceptanceManifest,
  useAcceptanceMode,
} from '../../features/acceptance';

const { Title, Text } = Typography;
const acceptanceActionIds = {
  list: 'PWD216F1-ACT-QUERY',
  approve: 'PWD216F1-ACT-APPROVE',
};
void acceptanceActionIds;

const Pwd216Form: React.FC = () => {
  const navigate = useNavigate();
  const acceptanceMode = useAcceptanceMode();
  const [detailSchemaPreview, setDetailSchemaPreview] = useState(false);

  // Query hook
  const {
    startNo, endNo, setStartNo, setEndNo,
    loading, items, fetchPending,
  } = usePwd216Query();

  // Approval hook
  const {
    checkedKeys, setCheckedKeys,
    selectedDocNo, setSelectedDocNo,
    editableItems, setEditableItems,
    updateAnomalyDesc,
    addDetailRow, deleteDetailRow, updateDetailField,
    lookupAnomalyCode,
    approving, handleApprove,
    selectedItem,
  } = usePwd216Approval(items);

  // Sync editableItems when query results change
  useEffect(() => {
    setEditableItems(items);
    setCheckedKeys([]);
    setSelectedDocNo(null);
  }, [items, setEditableItems, setCheckedKeys, setSelectedDocNo]);

  // Whether the selected item is checked (editable)
  const isSelectedEditable = selectedDocNo ? checkedKeys.includes(selectedDocNo) : false;

  // Approve callback
  const onApprove = useCallback(() => {
    handleApprove(fetchPending);
  }, [handleApprove, fetchPending]);

  // Lookup handler (async, but Table onBlur expects sync)
  const handleLookup = useCallback(
    (docNo: string, lineNo: number, code: string) => {
      lookupAnomalyCode(docNo, lineNo, code);
    },
    [lookupAnomalyCode],
  );

  return (
    <PermissionGuard module={Modules.PWD216} action="query">
      <div style={{ padding: '16px 24px 80px' }}>
        {/* Page Header */}
        <div style={{ marginBottom: 16 }}>
          <nav style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
            <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>
              首頁
            </a>
            <span style={{ margin: '0 4px' }}>/</span>
            <span>採購管理</span>
            <span style={{ margin: '0 4px' }}>/</span>
            <span>入庫單異常說明-採</span>
          </nav>
          <Title level={4} style={{ margin: 0 }}>
            入庫單異常說明-採
            <Tag color="blue" style={{ marginLeft: 8, verticalAlign: 'middle' }}>D216</Tag>
          </Title>
        </div>

        {/* Query Bar */}
        <Pwd216QueryBar
          startNo={startNo}
          endNo={endNo}
          loading={loading}
          onStartChange={setStartNo}
          onEndChange={setEndNo}
          onSearch={fetchPending}
        />

        {/* Summary & Approve Button */}
        <Card size="small" style={{ marginBottom: 12 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Text type="secondary">
                  共 <strong>{editableItems.length}</strong> 筆待核准
                </Text>
                {checkedKeys.length > 0 && (
                  <Text type="secondary">
                    已勾選 <strong>{checkedKeys.length}</strong> 筆
                  </Text>
                )}
              </Space>
            </Col>
            <Col>
              <Space>
                <Popconfirm
                  title={`確認存檔並核准 ${checkedKeys.length} 筆單據？`}
                  onConfirm={onApprove}
                  okText="確認核准"
                  cancelText="取消"
                  disabled={checkedKeys.length === 0}
                >
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    data-acceptance-action="PWD216F1-ACT-APPROVE"
                    loading={approving}
                    disabled={checkedKeys.length === 0}
                  >
                    存檔/核准
                  </Button>
                </Popconfirm>
                <Button
                  icon={<LogoutOutlined />}
                  onClick={() => navigate('/erp')}
                >
                  離開
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Grid1: Pending List */}
        <Card
          size="small"
          data-acceptance-action="PWD216F1-ACT-QUERY"
          title="待核准清單"
          style={{ marginBottom: 12 }}
          bodyStyle={{ padding: 0 }}
        >
          <Pwd216PendingList
            items={editableItems}
            loading={loading}
            checkedKeys={checkedKeys}
            selectedDocNo={selectedDocNo}
            onCheckedChange={setCheckedKeys}
            onSelect={setSelectedDocNo}
            onAnomalyDescChange={updateAnomalyDesc}
            isEditing={true}
          />
        </Card>

        {/* Grid2: Detail Panel — only visible when a row is selected */}
        {(selectedItem || detailSchemaPreview) && (
          <Card
            size="small"
            title={
              <span>
                異常說明明細 — 單號: <strong>{selectedItem?.doc_no || '欄位結構預覽'}</strong>
                {' '}
                <Tag color={selectedItem?.doc_type === 'D202' ? 'blue' : 'orange'}>
                  {selectedItem?.doc_type || '驗收'}
                </Tag>
              </span>
            }
          >
            <Pwd216DetailPanel
              details={selectedItem?.details || []}
              docNo={selectedItem?.doc_no || ''}
              isEditing={!!selectedItem && isSelectedEditable}
              onAddRow={addDetailRow}
              onDeleteRow={deleteDetailRow}
              onFieldChange={updateDetailField}
              onLookup={handleLookup}
            />
          </Card>
        )}
        {acceptanceMode.isEnabled(pwd216f1AcceptanceManifest.formId) && (
          <label data-acceptance-non-field style={{ display: 'inline-flex', gap: 6, marginTop: 8 }}>
            <input
              type="checkbox"
              data-contract-field="ui_detail_selected"
              checked={detailSchemaPreview}
              onChange={event => setDetailSchemaPreview(event.target.checked)}
            />
            驗收：顯示明細欄位結構
          </label>
        )}
        <BatchAcceptanceBridge
          manifest={pwd216f1AcceptanceManifest}
          records={editableItems}
          selectedKeys={checkedKeys}
          keyOf={(item) => item.doc_no}
          queryExact={async (key) => {
            const result = await pwd216Api.getPending(key, key);
            return result.data[0] || null;
          }}
          lastApiResponse={{ data: items, count: items.length }}
          loading={loading || approving}
          onAutoFill={() => {
            const first = editableItems[0];
            if (first) {
              setCheckedKeys([first.doc_no]);
              setSelectedDocNo(first.doc_no);
            }
          }}
          autoFillDisabled={editableItems.length === 0}
          autoFillReason="先查詢一筆待採購說明核准的異常單。"
          screenDraft={{ startNo, endNo, items: editableItems, selected_items: checkedKeys }}
        />
      </div>
    </PermissionGuard>
  );
};

export default Pwd216Form;
